import { COOKIES } from '@constants/cookies';
import { removeCookie } from '@services/cookies';
import { isSSR } from '@src/services/global';

const REDIRECT_LOOP_STORAGE_KEY = 'shopifyRedirectAttempts';
const REDIRECT_ERROR_PATH = '/redirect-error';
const REDIRECT_WINDOW_MS = 10 * 1000;
const MAX_REDIRECTS_PER_WINDOW = 3;

type RedirectReason = 'auth' | 'home' | 'not-found' | 'contact';

const getRecentRedirectAttempts = (): number[] => {
  if (isSSR) {
    return [];
  }

  try {
    const storedAttempts = sessionStorage.getItem(REDIRECT_LOOP_STORAGE_KEY);
    const parsedAttempts = storedAttempts ? JSON.parse(storedAttempts) : [];
    const now = Date.now();

    if (!Array.isArray(parsedAttempts)) {
      return [];
    }

    return parsedAttempts.filter(attempt => typeof attempt === 'number' && now - attempt <= REDIRECT_WINDOW_MS);
  } catch {
    return [];
  }
};

const setRedirectAttempts = (attempts: number[]) => {
  if (isSSR) {
    return;
  }

  sessionStorage.setItem(REDIRECT_LOOP_STORAGE_KEY, JSON.stringify(attempts));
};

const clearAuthCookies = () => {
  removeCookie(COOKIES.token);
  removeCookie(COOKIES.guestToken);
  removeCookie(COOKIES.basketId);
};

const redirectToRedirectErrorPage = (reason: RedirectReason) => {
  clearAuthCookies();

  const params = new URLSearchParams({
    reason,
    from: window.location.pathname,
  });

  window.location.replace(`${REDIRECT_ERROR_PATH}?${params.toString()}`);
};

interface NavigateToShopifyWithGuardOptions {
  reason: RedirectReason;
  redirectUrl: string | null;
  replace?: boolean;
}

export const navigateToShopifyWithGuard = ({
  reason,
  redirectUrl,
  replace = false,
}: NavigateToShopifyWithGuardOptions): boolean => {
  if (isSSR || !redirectUrl) {
    return false;
  }

  const attempts = getRecentRedirectAttempts();

  if (attempts.length >= MAX_REDIRECTS_PER_WINDOW) {
    redirectToRedirectErrorPage(reason);
    return true;
  }

  setRedirectAttempts([...attempts, Date.now()]);

  if (replace) {
    window.location.replace(redirectUrl);
    return true;
  }

  window.location.href = redirectUrl;
  return true;
};
